import type Phaser from "phaser";
import { POINT_LIFT, POINT_X, POINT_Y, abilityByNumber, advanceProjectiles, burstFrameAt, clearProjectiles, launchProjectile, newProjectileFlights, newProjectilePoint, projectileBurstMirrored, projectileElapsedMs, projectileFrame, projectileHeading, projectilePoint, projectileProgress, projectileStartFrame, resolveColour, type BurstFrame, type ProjectileLaunch, type ProjectilePoint, type ProjectilesRecord, type Rules } from "odyssey-shared";
import { RING_ALPHA, RING_MIN_SIXTEENTHS, ringPixelAlpha, ringTextureKey } from "./landing-ring.ts";
import type { DrawnLook, ProjectileLooks } from "./projectile-looks.ts";
import type { FlightReading, ProjectileReport } from "./projectile-report.ts";

/**
 * The shots the page draws (ADR-095 decisions 17 to 21): each launch of a `projectiles` frame flies its straight line
 * from its origin to its landing point, lands early by the page's smoothed round trip (`projectileLandsAt`) and plays
 * its look's burst there once. A projectile is a world sprite, not world UI and not an entity: it is drawn in the world
 * layer, has no handle, plate or selection, and is never hidden by the concealment rule or dimmed by the cone of sight,
 * so a shot always shows where it came from. A flight sorts by its ground point, as a character's feet. A burst is
 * drawn centred on the landing point and sorts half a pixel north of the line just south of the area it harms
 * (`burstSortLine`), so it draws over every character standing in that area or north of it, who looks inside it, and
 * under one standing south of it and under whatever a character there would walk behind, and ties with no character. World-attached UI (plates, bars, damage numbers) is
 * another layer under another camera and always draws over both.
 *
 * Under a shot whose burst is `RING_MIN_SIXTEENTHS` or wider lies the landing ring (`landing-ring.ts`), at the landing
 * point from the launch to the landing, under every sprite, so a dodge is a decision and not a guess. A look that lifts its sprite, in an arc or from the hand it leaves, has a small shadow on
 * the line.
 *
 * The state is `ProjectileFlights` (`odyssey-shared`), keyed by the shot's id, so a launch whose id is held replaces
 * its copy. The sprites are pools made at the scene's start, one a slot: 64 flights with their rings and shadows and 64
 * bursts, each doubled when its pool is, up to 256, beyond which the oldest slot is recycled. A drawn frame allocates
 * nothing; what an ability draws with is found once, at its first launch, and a sprite's depth is set only when the
 * line it sorts by changes its pixel row, since the engine boxes the number. An ability whose look the page lacks draws
 * nothing, with one warning. Every welcome empties the view.
 */

const SHADOW_ALPHA = 0.35;
const SHADOW_WIDTH = 8;
const SHADOW_HEIGHT = 4;
const BLANK_KEY = "projectile:blank";
const SHADOW_KEY = "projectile:shadow";
const NO_FRAME = -1;
const NO_ROW = -0x80000000;

/**
 * What the view is given: the scene it draws in, the looks the page holds, the rules that name each ability's look
 * and burst, how many world pixels a sixteenth of a tile is, the depth a sprite whose ground point is on a pixel line
 * draws at (a character's rule), the depth just over the ground and under everything that stands, the page's clock,
 * and where it reports.
 */
export interface ProjectileViewHost {
  readonly scene: Phaser.Scene;
  readonly looks: ProjectileLooks;
  readonly rules: Rules;
  readonly pixelsPerSixteenth: number;
  readonly depth: (groundY: number) => number;
  readonly groundDepth: number;
  readonly now: () => number;
  readonly report: ProjectileReport;
  readonly warn?: (line: string) => void;
}

/**
 * What an ability's shots draw with: the look, whether a ring lies under them, the ring's texture, the burst's radius
 * in world pixels, and the frames of the flight's strip and of each burst strip, found once, so a change of frame hands
 * the sprite a frame it need not look up or measure again (every frame of a strip is one size).
 */
interface AbilityDraw {
  readonly drawn: DrawnLook;
  readonly ringed: boolean;
  readonly ringKey: string;
  readonly radius: number;
  readonly flightFrames: readonly Phaser.Textures.Frame[];
  readonly burstFrames: readonly (readonly Phaser.Textures.Frame[])[];
}

/**
 * The frames of a strip's texture, in order.
 *
 * @param textures The texture manager.
 * @param key The strip's texture.
 * @param frames How many frames the strip has.
 * @returns The frames, or null when the texture lacks one.
 */
function stripFrames(textures: Phaser.Textures.TextureManager, key: string, frames: number): Phaser.Textures.Frame[] | null {
  const found: Phaser.Textures.Frame[] = [];
  for (let frame = 0; frame < frames; frame += 1) {
    const held = textures.exists(key) ? textures.getFrame(key, frame) : null;
    if (held === null || held === undefined) {
      return null;
    }
    found.push(held);
  }
  return found;
}

/**
 * Paints a texture pixel by pixel in one colour, each pixel at the alpha a rule gives it.
 *
 * @param textures The texture manager.
 * @param key The texture's key.
 * @param width Its width in pixels.
 * @param height Its height.
 * @param colour The colour as a CSS hex.
 * @param alphaAt A pixel's alpha, 0 to 1; a pixel at 0 is left clear.
 */
export function paintTexture(textures: Phaser.Textures.TextureManager, key: string, width: number, height: number, colour: string, alphaAt: (x: number, y: number) => number): void {
  if (textures.exists(key)) {
    return;
  }
  const canvas = textures.createCanvas(key, width, height);
  if (canvas === null) {
    return;
  }
  const context = canvas.getContext();
  context.fillStyle = colour;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const alpha = alphaAt(x, y);
      if (alpha > 0) {
        context.globalAlpha = alpha;
        context.fillRect(x, y, 1, 1);
      }
    }
  }
  canvas.refresh();
}

/**
 * The ground line a burst sorts by: one pixel south of the area it harms. The burst is drawn at the depth of a point
 * half a pixel north of the line, between the feet lines of two pixel rows, so a character whose feet stand anywhere in
 * that area, its southern edge included, or north of it, is drawn under the burst, one standing on the line or south of
 * it over the burst, and no character shares its depth, whatever order the sprites were made in.
 *
 * @param landingY The landing point's row in world pixels.
 * @param radius The burst's radius in world pixels.
 * @returns The line in world pixels.
 */
export function burstSortLine(landingY: number, radius: number): number {
  return landingY + radius + 1;
}

/**
 * The view.
 */
export class ProjectileView {
  private readonly store = newProjectileFlights();
  private readonly byAbility = new Map<number, AbilityDraw | null>();
  private readonly flightSprites: Phaser.GameObjects.Image[] = [];
  private readonly ringSprites: Phaser.GameObjects.Image[] = [];
  private readonly shadowSprites: Phaser.GameObjects.Image[] = [];
  private readonly burstSprites: Phaser.GameObjects.Image[] = [];
  private readonly flightKeys: string[] = [];
  private readonly ringKeys: string[] = [];
  private readonly burstKeys: string[] = [];
  private readonly flightFrames: Int16Array;
  private readonly burstFrames: Int16Array;
  private readonly flightRows: Int32Array;
  private readonly burstRows: Int32Array;
  private readonly point: ProjectilePoint = newProjectilePoint();
  private readonly burstFrame: BurstFrame = { strip: 0, frame: 0 };
  private readonly injected: ProjectilesRecord = { tick: 0, count: 0, launches: [] };
  private readonly lacking: string[] = [];
  private readonly told: Float64Array;
  private undrawn = 0;

  /**
   * Told once of each shot landing, as its burst starts (P2.8-T84): the ability's number and the landing point in
   * sixteenths. Only a shot whose launch the page received lands, so a shot never seen thrown is never told. A landing
   * while it is null is never told later.
   */
  public onLanded: ((ability: number, x: number, y: number) => void) | null = null;

  /**
   * @param host The scene, the looks, the rules, the scale, the depths, the clock and the report.
   */
  public constructor(private readonly host: ProjectileViewHost) {
    const textures = host.scene.textures;
    paintTexture(textures, BLANK_KEY, 1, 1, "#000000", () => 0);
    paintTexture(textures, SHADOW_KEY, SHADOW_WIDTH, SHADOW_HEIGHT, "#000000", (x, y) => ((x + 0.5 - SHADOW_WIDTH / 2) / (SHADOW_WIDTH / 2)) ** 2 + ((y + 0.5 - SHADOW_HEIGHT / 2) / (SHADOW_HEIGHT / 2)) ** 2 <= 1 ? 1 : 0);
    this.flightFrames = new Int16Array(this.store.flights.free.length).fill(NO_FRAME);
    this.burstFrames = new Int16Array(this.store.bursts.free.length).fill(NO_FRAME);
    this.flightRows = new Int32Array(this.store.flights.free.length).fill(NO_ROW);
    this.burstRows = new Int32Array(this.store.bursts.free.length).fill(NO_ROW);
    this.told = new Float64Array(this.store.bursts.free.length).fill(Number.NaN);
    this.grow();
    const report = host.report;
    report.looks = () => host.looks.ids();
    report.lacking = () => [...this.lacking];
    report.list = () => this.list();
    report.launch = (launches, rttMs) => this.inject(launches, rttMs);
    this.count();
  }

  /**
   * Takes a `projectiles` frame as it arrives: each launch whose ability has a look the page holds starts a flight,
   * or replaces the flight of its id.
   *
   * @param record The decoded frame; read and not kept.
   * @param rttMs The connection's smoothed round trip in ms, null while it has measured none.
   */
  public receive(record: Readonly<ProjectilesRecord>, rttMs: number | null): void {
    const now = this.host.now();
    for (let index = 0; index < record.count; index += 1) {
      const launch = record.launches[index] as Readonly<ProjectileLaunch>;
      const draw = this.drawOf(launch.ability);
      if (draw === null) {
        this.undrawn += 1;
        continue;
      }
      launchProjectile(this.store, launch, now, rttMs, draw.ringed, draw.drawn.burstMs);
    }
  }

  /**
   * Draws the frame: lands the flights whose time has come, ends the bursts that have played out, makes the sprites
   * of a pool that grew, and places every flight with its ring and shadow and every burst.
   */
  public update(): void {
    const now = this.host.now();
    advanceProjectiles(this.store, now);
    this.tellLandings();
    this.grow();
    this.drawFlights(now);
    this.drawBursts(now);
    this.count();
  }

  /**
   * Empties the view, as every welcome does: every flight, ring, shadow and burst is dropped and hidden. The sprites
   * stay in their pools.
   */
  public clear(): void {
    clearProjectiles(this.store);
    for (let slot = 0; slot < this.flightSprites.length; slot += 1) {
      (this.flightSprites[slot] as Phaser.GameObjects.Image).visible = false;
      (this.ringSprites[slot] as Phaser.GameObjects.Image).visible = false;
      (this.shadowSprites[slot] as Phaser.GameObjects.Image).visible = false;
    }
    for (let slot = 0; slot < this.burstSprites.length; slot += 1) {
      (this.burstSprites[slot] as Phaser.GameObjects.Image).visible = false;
    }
    this.count();
  }

  /**
   * Destroys every sprite, as the scene shuts down, and takes from the report everything that holds the view or its
   * host.
   */
  public destroy(): void {
    for (const sprites of [this.flightSprites, this.ringSprites, this.shadowSprites, this.burstSprites]) {
      for (const sprite of sprites.splice(0)) {
        sprite.destroy();
      }
    }
    const report = this.host.report;
    report.launch = null;
    report.list = () => [];
    report.looks = () => [];
    report.lacking = () => [];
  }

  /**
   * What an ability's shots draw with, found once: its look from the rules' shape and the page's looks, and its ring
   * when its burst is wide enough. An ability that is no projectile, or whose look the page lacks, draws nothing, with
   * one warning.
   *
   * @param ability The ability's number, as the launch carries it.
   * @returns What it draws with, or null.
   */
  private drawOf(ability: number): AbilityDraw | null {
    const known = this.byAbility.get(ability);
    if (known !== undefined) {
      return known;
    }
    const shape = abilityByNumber(this.host.rules, ability)?.shape;
    const warn = this.host.warn ?? console.warn;
    let draw: AbilityDraw | null = null;
    if (shape === undefined || shape.kind !== "projectile") {
      warn(`ignoring the shots of ability ${ability}, which the page's rules do not hold as a projectile`);
    } else {
      const textures = this.host.scene.textures;
      const drawn = this.host.looks.get(shape.look);
      const flightFrames = drawn === null ? null : stripFrames(textures, drawn.flightKey, drawn.look.flight.frames);
      const burstFrames = drawn === null ? [] : drawn.look.burst.map((strip, index) => stripFrames(textures, drawn.burstKeys[index] as string, strip.frames));
      if (drawn === null || flightFrames === null || burstFrames.includes(null)) {
        this.lacking.push(shape.look);
        warn(`the page lacks the projectile look ${shape.look}; its shots are not drawn`);
      } else {
        const radius = Math.round(shape.radiusSixteenths * this.host.pixelsPerSixteenth);
        const ringed = shape.radiusSixteenths >= RING_MIN_SIXTEENTHS;
        if (ringed) {
          paintTexture(textures, ringTextureKey(radius), 2 * radius + 1, 2 * radius + 1, resolveColour("danger"), (x, y) => ringPixelAlpha(x, y, radius));
        }
        draw = { drawn, ringed, ringKey: ringed ? ringTextureKey(radius) : BLANK_KEY, radius, flightFrames, burstFrames: burstFrames as Phaser.Textures.Frame[][] };
      }
    }
    this.byAbility.set(ability, draw);
    return draw;
  }

  /**
   * Tells `onLanded` of each burst that started since the last frame, once: a burst slot is told when its start time
   * differs from the one last told for that slot, so a burst playing on is never told again.
   */
  private tellLandings(): void {
    const store = this.store;
    const bursts = store.bursts;
    const told = this.told;
    for (let slot = 0; slot < bursts.capacity; slot += 1) {
      const started = store.burstStartMs[slot] as number;
      if (bursts.live[slot] !== 1 || told[slot] === started) {
        continue;
      }
      told[slot] = started;
      this.onLanded?.(store.burstAbility[slot] as number, store.burstX[slot] as number, store.burstY[slot] as number);
    }
  }

  /**
   * Makes the sprites of the slots a pool gained: a flight, its ring and its shadow for each flight slot, and a burst
   * for each burst slot, all hidden.
   */
  private grow(): void {
    const scene = this.host.scene;
    while (this.flightSprites.length < this.store.flights.capacity) {
      this.ringSprites.push(scene.add.image(0, 0, BLANK_KEY).setAlpha(RING_ALPHA).setDepth(this.host.groundDepth).setVisible(false));
      this.shadowSprites.push(scene.add.image(0, 0, SHADOW_KEY).setAlpha(SHADOW_ALPHA).setDepth(this.host.groundDepth).setVisible(false));
      this.flightSprites.push(scene.add.image(0, 0, BLANK_KEY).setVisible(false));
      this.flightKeys.push(BLANK_KEY);
      this.ringKeys.push(BLANK_KEY);
    }
    while (this.burstSprites.length < this.store.bursts.capacity) {
      this.burstSprites.push(scene.add.image(0, 0, BLANK_KEY).setVisible(false));
      this.burstKeys.push(BLANK_KEY);
    }
  }

  /**
   * Places every flight, its ring and its shadow, and hides those of a free slot.
   *
   * @param now The time drawn.
   */
  private drawFlights(now: number): void {
    const store = this.store;
    const scale = this.host.pixelsPerSixteenth;
    const point = this.point;
    for (let slot = 0; slot < store.flights.capacity; slot += 1) {
      const sprite = this.flightSprites[slot] as Phaser.GameObjects.Image;
      const ring = this.ringSprites[slot] as Phaser.GameObjects.Image;
      const shadow = this.shadowSprites[slot] as Phaser.GameObjects.Image;
      const draw = store.flights.live[slot] === 1 ? this.byAbility.get(store.ability[slot] as number) : undefined;
      if (draw === undefined || draw === null) {
        sprite.visible = false;
        ring.visible = false;
        shadow.visible = false;
        continue;
      }
      const look = draw.drawn.look;
      const originX = store.originX[slot] as number;
      const originY = store.originY[slot] as number;
      const landingX = store.landingX[slot] as number;
      const landingY = store.landingY[slot] as number;
      const age = store.age[slot] as number;
      const arrival = store.arrivalMs[slot] as number;
      const covered = projectileProgress(now, arrival, store.landsAtMs[slot] as number, store.flightTicks[slot] as number, age);
      projectilePoint(originX, originY, landingX, landingY, covered, look.arcPixels, look.launchPixels, point);
      const groundX = Math.round((point[POINT_X] as number) * scale);
      const groundY = Math.round((point[POINT_Y] as number) * scale);
      const frames = look.flight.frames;
      const frame = projectileFrame(projectileStartFrame(store.variation[slot] as number, frames), projectileElapsedMs(now, arrival, age), look.flight.frameMs, frames);
      if (this.flightKeys[slot] !== draw.drawn.flightKey) {
        this.flightKeys[slot] = draw.drawn.flightKey;
        this.flightFrames[slot] = frame;
        sprite.setTexture(draw.drawn.flightKey, frame);
      } else if (this.flightFrames[slot] !== frame) {
        this.flightFrames[slot] = frame;
        sprite.setFrame(draw.flightFrames[frame] as Phaser.Textures.Frame, false, false);
      }
      sprite.x = groundX;
      sprite.y = groundY - Math.round(point[POINT_LIFT] as number);
      sprite.rotation = look.rotates ? projectileHeading(originX, originY, landingX, landingY) : 0;
      if (this.flightRows[slot] !== groundY) {
        this.flightRows[slot] = groundY;
        sprite.depth = this.host.depth(groundY);
      }
      sprite.visible = true;
      ring.visible = draw.ringed && store.ringed[slot] === 1;
      if (ring.visible) {
        if (this.ringKeys[slot] !== draw.ringKey) {
          this.ringKeys[slot] = draw.ringKey;
          ring.setTexture(draw.ringKey);
        }
        ring.x = Math.round(landingX * scale);
        ring.y = Math.round(landingY * scale);
      }
      shadow.visible = look.arcPixels > 0 || look.launchPixels > 0;
      if (shadow.visible) {
        shadow.x = groundX;
        shadow.y = groundY;
      }
    }
  }

  /**
   * Places every burst at its landing point on the frame its time gives, mirrored as its shot's variation says, at the
   * depth half a pixel north of the line just south of the area it harms, and hides those of a free slot.
   *
   * @param now The time drawn.
   */
  private drawBursts(now: number): void {
    const store = this.store;
    const scale = this.host.pixelsPerSixteenth;
    const shown = this.burstFrame;
    for (let slot = 0; slot < store.bursts.capacity; slot += 1) {
      const sprite = this.burstSprites[slot] as Phaser.GameObjects.Image;
      const draw = store.bursts.live[slot] === 1 ? this.byAbility.get(store.burstAbility[slot] as number) : undefined;
      if (draw === undefined || draw === null || !burstFrameAt(draw.drawn.look, now - (store.burstStartMs[slot] as number), shown)) {
        sprite.visible = false;
        continue;
      }
      const key = draw.drawn.burstKeys[shown.strip] as string;
      if (this.burstKeys[slot] !== key) {
        this.burstKeys[slot] = key;
        this.burstFrames[slot] = shown.frame;
        sprite.setTexture(key, shown.frame);
      } else if (this.burstFrames[slot] !== shown.frame) {
        this.burstFrames[slot] = shown.frame;
        sprite.setFrame((draw.burstFrames[shown.strip] as readonly Phaser.Textures.Frame[])[shown.frame] as Phaser.Textures.Frame, false, false);
      }
      const groundY = Math.round((store.burstY[slot] as number) * scale);
      const line = burstSortLine(groundY, draw.radius);
      sprite.x = Math.round((store.burstX[slot] as number) * scale);
      sprite.y = groundY;
      sprite.flipX = projectileBurstMirrored(store.burstVariation[slot] as number);
      if (this.burstRows[slot] !== line) {
        this.burstRows[slot] = line;
        sprite.depth = this.host.depth(line - 0.5);
      }
      sprite.visible = true;
    }
  }

  /**
   * Writes the counts into the report.
   */
  private count(): void {
    const store = this.store;
    const report = this.host.report;
    let rings = 0;
    for (let slot = 0; slot < this.ringSprites.length; slot += 1) {
      rings += (this.ringSprites[slot] as Phaser.GameObjects.Image).visible ? 1 : 0;
    }
    report.flights = store.flights.count;
    report.bursts = store.bursts.count;
    report.rings = rings;
    report.flightSprites = this.flightSprites.length;
    report.ringSprites = this.ringSprites.length;
    report.burstSprites = this.burstSprites.length;
    report.launched = store.launched;
    report.replaced = store.replaced;
    report.ignored = store.ignored;
    report.recycled = store.recycled + store.burstsRecycled;
    report.undrawn = this.undrawn;
  }

  /**
   * Takes launches a browser suite hands the report, as a frame's would be taken.
   *
   * @param launches The launches.
   * @param rttMs The round trip to land them by, null for none.
   */
  private inject(launches: readonly Readonly<ProjectileLaunch>[], rttMs: number | null): void {
    const record = this.injected;
    record.launches.length = 0;
    for (const launch of launches) {
      record.launches.push({ ...launch });
    }
    record.count = launches.length;
    this.receive(record, rttMs);
  }

  /**
   * Every flight and burst showing, for a browser suite.
   *
   * @returns The readings, flights first.
   */
  private list(): FlightReading[] {
    const store = this.store;
    const readings: FlightReading[] = [];
    for (let slot = 0; slot < this.flightSprites.length; slot += 1) {
      const sprite = this.flightSprites[slot] as Phaser.GameObjects.Image;
      if (sprite.visible) {
        const ring = this.ringSprites[slot] as Phaser.GameObjects.Image;
        const shadow = this.shadowSprites[slot] as Phaser.GameObjects.Image;
        readings.push({ kind: "flight", id: store.id[slot] as number, ability: store.ability[slot] as number, x: sprite.x, y: sprite.y, depth: sprite.depth, texture: sprite.texture.key, frame: Number(sprite.frame.name), rotation: sprite.rotation, mirrored: false, ring: ring.visible ? { x: ring.x, y: ring.y, width: ring.width, alpha: ring.alpha, depth: ring.depth, texture: ring.texture.key } : null, shadow: shadow.visible ? { x: shadow.x, y: shadow.y } : null, landsInMs: (store.landsAtMs[slot] as number) - this.host.now(), flightTicks: store.flightTicks[slot] as number, age: store.age[slot] as number, arrivalMs: store.arrivalMs[slot] as number, landsAtMs: store.landsAtMs[slot] as number });
      }
    }
    for (let slot = 0; slot < this.burstSprites.length; slot += 1) {
      const sprite = this.burstSprites[slot] as Phaser.GameObjects.Image;
      if (sprite.visible) {
        readings.push({ kind: "burst", id: store.burstId[slot] as number, ability: store.burstAbility[slot] as number, x: sprite.x, y: sprite.y, depth: sprite.depth, texture: sprite.texture.key, frame: Number(sprite.frame.name), rotation: 0, mirrored: sprite.flipX, ring: null, shadow: null, landsInMs: (store.burstStartMs[slot] as number) - this.host.now(), flightTicks: 0, age: 0, arrivalMs: store.burstStartMs[slot] as number, landsAtMs: store.burstStartMs[slot] as number });
      }
    }
    return readings;
  }
}
